import { CUKCUK_DEFAULT_CITY } from '../constants/cukcuk';
import type { Customization, CustomizationGroup, DishInfo } from '../types/customization';
import type { Dish } from '../types/dish';
import type {
  CukcukParseResult,
  DishOption,
  MenuOption,
  MenuOptionRef,
} from '../types/cukcukImport';
import type { InvoiceNotification } from '../types/invoiceNotification';
import type { CukcukImportHistoryCounts } from '../types/cukcukImportHistory';
import { CustomizationService } from './CustomizationService';
import {
  createCukcukCustomizationService,
  createCukcukDishService,
  createCukcukInvoiceNotificationService,
} from './cukcukDataServices';
import { CukcukImportHistoryService } from './cukcukImportHistoryService';
import { DishService } from './dishService';
import { InvoiceNotificationService } from './invoiceNotificationService';
import {
  buildMenuOptionCatalogFromCustomizations,
  buildMenuOptionCatalogFromDishes,
  cukcukInvoicesToNotifications,
  parseCukcukInvoicesWithOptionMapping,
  resolveOptionCatalog,
  type MappedCukcukInvoice,
  type OptionCatalogSources,
  type ParseCukcukInvoicesWithMappingResult,
} from '../utils/cukcukInvoiceImportUtils';
import type { MappedCukcukInvoiceLineItem } from '../utils/invoiceOptionMapper';
import { mapCukcukOptionWithScore, OptionCatalogIndex } from '../utils/optionMatcher';
import {
  buildCukcukOptionCode,
  inferCukcukStpvGroup,
  parseCukcukMenu,
  type ParseCukcukMenuOptions,
} from '../utils/cukcukParser';
import { computeFileFingerprint } from '../utils/fileFingerprint';

const DEFAULT_CITY = CUKCUK_DEFAULT_CITY;
const CUKCUK_CUSTOMIZATION_PREFIX = 'CUKCUK STPV - ';

const defaultPriceConfiguration = (): Dish['priceConfiguration'] => ({
  allowPriceEdit: false,
  requireQuantity: false,
  allowRemoveWithoutPermission: false,
});

export interface CukcukImportItemResult {
  added: number;
  updated: number;
  errors: string[];
}

export interface CukcukImportLogContext {
  fileName?: string;
  fileHash?: string;
  fileSize?: number;
}

export interface CukcukMenuImportResult {
  dishes: CukcukImportItemResult;
  options: CukcukImportItemResult;
  relations: CukcukImportItemResult;
  errors: string[];
  historyId?: string;
}

export interface CukcukInvoiceImportResult {
  added: number;
  updated: number;
  skipped: number;
  lineItems: number;
  options: number;
  /** Option chưa có trong hệ thống đã được tự tạo (giá 0) trước khi lưu HĐ. */
  autoCreatedOptions: CukcukImportItemResult;
  /** Cảnh báo cho admin rà soát option tự tạo / chưa gán nhóm customization. */
  optionWarnings: string[];
  errors: string[];
  historyId?: string;
}

export interface CukcukMenuFileImportResult extends CukcukMenuImportResult {
  parseWarnings: string[];
  fileName: string;
}

export interface CukcukInvoiceFileImportResult extends CukcukInvoiceImportResult {
  mappingWarnings: string[];
  fileName: string;
}

export type CukcukParsedInvoicesInput =
  | MappedCukcukInvoice[]
  | ParseCukcukInvoicesWithMappingResult;

const sumImportItemResult = (item: CukcukImportItemResult): number => item.added + item.updated;

export const buildMenuImportHistoryCounts = (result: CukcukMenuImportResult): CukcukImportHistoryCounts => {
  const dishes = sumImportItemResult(result.dishes);
  const options = sumImportItemResult(result.options);
  const relations = sumImportItemResult(result.relations);

  return {
    total: dishes + options + relations,
    dishes,
    options,
    relations,
  };
};

export const buildInvoiceImportHistoryCounts = (
  result: CukcukInvoiceImportResult,
): CukcukImportHistoryCounts => {
  const lineItems = result.added + result.updated;

  return {
    total: lineItems,
    lineItems,
    invoiceOptions: result.options,
    autoCreatedOptions: sumImportItemResult(result.autoCreatedOptions),
  };
};

export const isSuccessfulMenuImport = (result: CukcukMenuImportResult): boolean =>
  buildMenuImportHistoryCounts(result).total > 0;

export const isSuccessfulInvoiceImport = (result: CukcukInvoiceImportResult): boolean =>
  result.added + result.updated > 0;

const normalizeCodeKey = (value: string): string => value.trim().toLowerCase();

const normalizeNameKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

interface DishIndexes {
  byCode: Map<string, Dish>;
  duplicateCodes: Set<string>;
  byName: Map<string, Dish>;
}

const buildDishIndexes = (dishes: Dish[]): DishIndexes => {
  const byCode = new Map<string, Dish>();
  const duplicateCodes = new Set<string>();
  const byName = new Map<string, Dish>();

  dishes.forEach((dish) => {
    const code = dish.code?.trim();
    if (code) {
      const key = normalizeCodeKey(code);
      if (byCode.has(key)) duplicateCodes.add(key);
      else byCode.set(key, dish);
    }

    const nameKey = dish.name ? normalizeNameKey(dish.name) : '';
    if (nameKey && !byName.has(nameKey)) {
      byName.set(nameKey, dish);
    }
  });

  return { byCode, duplicateCodes, byName };
};

const findDishByCodeOrName = (
  indexes: DishIndexes,
  code?: string,
  name?: string,
): Dish | undefined => {
  const normalizedCode = code?.trim();
  if (normalizedCode) {
    const matched = indexes.byCode.get(normalizeCodeKey(normalizedCode));
    if (matched) return matched;
  }

  const normalizedName = name ? normalizeNameKey(name) : '';
  if (normalizedName) {
    return indexes.byName.get(normalizedName);
  }

  return undefined;
};

const omitUndefinedFields = <T extends Record<string, unknown>>(value: T): Partial<T> =>
  Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined)) as Partial<T>;

const mergeDish = (existing: Dish | undefined, incoming: Partial<Dish>): Dish => {
  const base = existing ?? ({} as Dish);
  const definedIncoming = omitUndefinedFields(incoming as Record<string, unknown>);
  return {
    ...base,
    ...definedIncoming,
    code: incoming.code || base.code,
    name: incoming.name || base.name,
    city: incoming.city || base.city || DEFAULT_CITY,
    price: incoming.price ?? base.price ?? 0,
    unit: incoming.unit || base.unit || 'Phần',
    vat: incoming.vat ?? base.vat ?? 0,
    preparationTime: incoming.preparationTime ?? base.preparationTime ?? 0,
    priceConfiguration: incoming.priceConfiguration || base.priceConfiguration || defaultPriceConfiguration(),
    isActive: incoming.isActive ?? base.isActive ?? true,
  };
};

const parsedDishToDish = (parsed: CukcukParseResult['dishes'][number]): Dish => {
  const partial = parsed.dish;
  return mergeDish(undefined, {
    code: partial.code || '',
    name: partial.name || '',
    price: partial.price ?? 0,
    unit: partial.unit || 'Phần',
    city: partial.city || DEFAULT_CITY,
    vat: partial.vat ?? 0,
    preparationTime: partial.preparationTime ?? 0,
    isActive: partial.isActive ?? true,
    barcode: partial.barcode,
    description: partial.description,
    group: partial.group,
    groupName: partial.groupName,
    type: partial.type,
    typeName: partial.typeName,
    displayOrder: partial.displayOrder,
    priceConfiguration: partial.priceConfiguration || defaultPriceConfiguration(),
  });
};

const menuOptionToDish = (option: MenuOption, city: string): Dish =>
  mergeDish(undefined, {
    code: option.code,
    name: option.name,
    price: option.price,
    unit: option.unit || 'Phần',
    city,
    vat: option.vat ?? 0,
    preparationTime: 0,
    isActive: option.isActive,
    group: option.optionGroupName,
    groupName: option.optionGroupName,
    displayOrder: option.displayOrder,
    description: option.description,
    isVirtualDish: true,
    priceConfiguration: defaultPriceConfiguration(),
  });

const customizationNameForDish = (dishCode: string): string => `${CUKCUK_CUSTOMIZATION_PREFIX}${dishCode}`;

const findCustomizationForDish = (
  existing: Customization[],
  dishCode: string,
): Customization | undefined => {
  const targetName = customizationNameForDish(dishCode);

  const byName = existing.find((item) => item.name === targetName);
  if (byName) return byName;

  return existing.find(
    (item) =>
      Array.isArray(item.dishCode) &&
      item.dishCode.length === 1 &&
      item.dishCode[0]?.trim() === dishCode,
  );
};

const mapOptionRefToDishInfo = (option: MenuOptionRef, dishIndexes: DishIndexes): DishInfo => {
  const matchedDish = findDishByCodeOrName(dishIndexes, option.code, option.name);

  return {
    code: option.code,
    name: matchedDish?.name || option.name,
    price: matchedDish?.price ?? option.price,
    isActive: option.isActive && matchedDish?.isActive !== false,
  };
};

const buildCustomizationGroups = (
  dishOptions: DishOption[],
  dishCode: string,
  dishIndexes: DishIndexes,
): CustomizationGroup[] => {
  const groupsForDish = dishOptions.filter((item) => item.dishCode === dishCode);

  return groupsForDish.map((relation) => ({
    groupName: relation.groupName,
    isRequired: relation.isRequired,
    limit: relation.limit,
    dishCodes: relation.options.map((option) => mapOptionRefToDishInfo(option, dishIndexes)),
  }));
};

const upsertDish = async (
  dishService: DishService,
  indexes: DishIndexes,
  incoming: Dish,
  label: string,
  result: CukcukImportItemResult,
): Promise<void> => {
  if (!incoming.code?.trim() && !incoming.name?.trim()) {
    result.errors.push(`${label}: thiếu mã và tên món.`);
    return;
  }

  try {
    const codeKey = incoming.code?.trim() ? normalizeCodeKey(incoming.code) : '';
    if (codeKey && indexes.duplicateCodes.has(codeKey)) {
      result.errors.push(`${label}: trùng mã chuẩn hóa ${incoming.code}.`);
      return;
    }
    const existing = codeKey
      ? indexes.byCode.get(codeKey)
      : findDishByCodeOrName(indexes, undefined, incoming.name);
    const payload = mergeDish(existing, incoming);

    if (existing?.id) {
      await dishService.updateDish(existing.id, payload);
      result.updated += 1;
      indexes.byCode.set(normalizeCodeKey(payload.code), { ...existing, ...payload });
      indexes.byName.set(normalizeNameKey(payload.name), { ...existing, ...payload });
      return;
    }

    const created = await dishService.addDish(payload);
    result.added += 1;
    indexes.byCode.set(normalizeCodeKey(created.code), created);
    indexes.byName.set(normalizeNameKey(created.name), created);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    result.errors.push(`${label} (${incoming.code || incoming.name}): ${message}`);
  }
};

const normalizeParsedInvoices = (input: CukcukParsedInvoicesInput): MappedCukcukInvoice[] =>
  Array.isArray(input) ? input : input.invoices;

const AUTO_OPTION_DESCRIPTION = 'Tự động tạo khi import hóa đơn CUKCUK — vui lòng rà soát giá và nhóm tùy chọn.';

interface UnmappedInvoiceOption {
  optionName: string;
  optionCode: string;
  parentDishCodes: string[];
}

const collectUnmappedInvoiceOptions = (invoices: MappedCukcukInvoice[]): UnmappedInvoiceOption[] => {
  const seen = new Map<string, UnmappedInvoiceOption>();

  invoices.forEach((invoice) => {
    invoice.items.forEach((item) => {
      item.selectedOptions.forEach((option) => {
        if (option.mappedOption || !option.optionName.trim()) return;

        const optionName = option.optionName.trim();
        const key = normalizeNameKey(optionName);
        const parentCode = item.itemId?.trim();

        if (seen.has(key)) {
          const existing = seen.get(key)!;
          if (parentCode && !existing.parentDishCodes.includes(parentCode)) {
            existing.parentDishCodes.push(parentCode);
          }
          return;
        }

        seen.set(key, {
          optionName,
          optionCode: option.optionId || buildCukcukOptionCode(optionName, parentCode),
          parentDishCodes: parentCode ? [parentCode] : [],
        });
      });
    });
  });

  return Array.from(seen.values());
};

const unmappedInvoiceOptionToDish = (option: UnmappedInvoiceOption, city: string): Dish => {
  const inferred = inferCukcukStpvGroup(option.optionName);

  return mergeDish(undefined, {
    code: option.optionCode,
    name: option.optionName,
    price: 0,
    unit: 'Phần',
    city,
    vat: 0,
    preparationTime: 0,
    isActive: true,
    isVirtualDish: true,
    group: inferred.groupName,
    groupName: inferred.groupName,
    description: AUTO_OPTION_DESCRIPTION,
    priceConfiguration: defaultPriceConfiguration(),
  });
};

const unmappedOptionToMenuOption = (option: UnmappedInvoiceOption): MenuOption => {
  const inferred = inferCukcukStpvGroup(option.optionName);

  return {
    code: option.optionCode,
    name: option.optionName,
    price: 0,
    isActive: true,
    optionGroupName: inferred.groupName,
    source: {
      cukcukCode: option.optionCode,
      cukcukName: option.optionName,
      cukcukGroupName: inferred.groupName,
    },
  };
};

const remapInvoiceSelectedOptions = (
  selectedOptions: MappedCukcukInvoiceLineItem['selectedOptions'],
  catalog: OptionCatalogIndex,
  parentItemId?: string,
): void => {
  selectedOptions.forEach((option) => {
    if (option.mappedOption) return;

    const match = mapCukcukOptionWithScore(option.optionName, catalog, parentItemId);
    if (!match.option) return;

    option.mappedOption = match.option;
    option.optionId = match.option.code;
    option.matchScore = match.score;
    option.matchMethod = match.method;
  });
};

const appendOptionToCustomizationGroup = async (
  customizationService: CustomizationService,
  existingCustomizations: Customization[],
  dishCode: string,
  option: UnmappedInvoiceOption,
  dishIndexes: DishIndexes,
  city: string,
): Promise<void> => {
  const inferred = inferCukcukStpvGroup(option.optionName);
  const dishInfo = mapOptionRefToDishInfo(
    {
      code: option.optionCode,
      name: option.optionName,
      price: 0,
      isActive: true,
    },
    dishIndexes,
  );

  const existing = findCustomizationForDish(existingCustomizations, dishCode);
  const groups = existing?.groups ? [...existing.groups] : [];
  const groupIndex = groups.findIndex((group) => group.groupName === inferred.groupName);

  if (groupIndex >= 0) {
    const group = groups[groupIndex];
    const alreadyLinked = (group.dishCodes || []).some((item) => item.code === option.optionCode);
    if (!alreadyLinked) {
      groups[groupIndex] = {
        ...group,
        dishCodes: [...(group.dishCodes || []), dishInfo],
      };
    }
  } else {
    groups.push({
      groupName: inferred.groupName,
      isRequired: inferred.isRequired,
      limit: inferred.limit,
      dishCodes: [dishInfo],
    });
  }

  const payload: Customization = {
    name: customizationNameForDish(dishCode),
    city: findDishByCodeOrName(dishIndexes, dishCode)?.city || city,
    dishCode: [dishCode],
    groups,
  };

  if (existing?.id) {
    await customizationService.updateCustomization(existing.id, payload);
    const index = existingCustomizations.findIndex((item) => item.id === existing.id);
    if (index >= 0) existingCustomizations[index] = { ...existing, ...payload };
    return;
  }

  const created = await customizationService.addCustomization(payload);
  existingCustomizations.push(created);
};

/**
 * Tự tạo option thiếu (món ảo, giá 0) rồi gán lại mappedOption trên hóa đơn.
 * Tự tạo option thiếu, gán vào customization món cha khi có thể.
 */
const prepareInvoicesWithAutoCreatedOptions = async (
  dishService: DishService,
  customizationService: CustomizationService,
  invoices: MappedCukcukInvoice[],
  city = DEFAULT_CITY,
  allowAutoCreate = true,
): Promise<{
  autoCreatedOptions: CukcukImportItemResult;
  optionWarnings: string[];
  createdCatalog: MenuOption[];
}> => {
  const autoCreatedOptions: CukcukImportItemResult = { added: 0, updated: 0, errors: [] };
  const optionWarnings: string[] = [];
  const createdCatalog: MenuOption[] = [];

  const existingDishes = await dishService.getAllDishes();
  const dishIndexes = buildDishIndexes(existingDishes);
  const existingCustomizations = await customizationService.getAllCustomizations();
  const fullCatalog = new OptionCatalogIndex([
    ...buildMenuOptionCatalogFromDishes(existingDishes),
    ...buildMenuOptionCatalogFromCustomizations(existingCustomizations),
  ]);

  invoices.forEach((invoice) => {
    invoice.items.forEach((item) => {
      remapInvoiceSelectedOptions(item.selectedOptions, fullCatalog, item.itemId);
    });
  });

  let unmapped = collectUnmappedInvoiceOptions(invoices);
  if (!unmapped.length || !allowAutoCreate) {
    return { autoCreatedOptions, optionWarnings, createdCatalog };
  }

  for (const option of unmapped) {
    const existingMatch = fullCatalog.lookup(option.optionName, option.parentDishCodes[0]);
    if (existingMatch.option) {
      continue;
    }

    const dish = unmappedInvoiceOptionToDish(option, city);
    const addedBefore = autoCreatedOptions.added;
    const updatedBefore = autoCreatedOptions.updated;

    await upsertDish(dishService, dishIndexes, dish, 'Option (tự tạo)', autoCreatedOptions);

    const menuOption = unmappedOptionToMenuOption(option);
    createdCatalog.push(menuOption);

    for (const parentDishCode of option.parentDishCodes) {
      try {
        await appendOptionToCustomizationGroup(
          customizationService,
          existingCustomizations,
          parentDishCode,
          option,
          dishIndexes,
          city,
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        optionWarnings.push(`Không gán option "${option.optionName}" vào món ${parentDishCode}: ${message}`);
      }
    }

    if (autoCreatedOptions.added > addedBefore) {
      optionWarnings.push(
        `Đã tự tạo option "${option.optionName}" (${option.optionCode}, giá 0) và gán vào customization món cha.`,
      );
      continue;
    }

    if (autoCreatedOptions.updated > updatedBefore) {
      optionWarnings.push(
        `Option "${option.optionName}" đã tồn tại theo tên/mã — dùng bản ghi hiện có.`,
      );
    }
  }

  if (createdCatalog.length) {
    const remapCatalog = new OptionCatalogIndex([
      ...fullCatalog.getAll(),
      ...createdCatalog,
    ]);

    invoices.forEach((invoice) => {
      invoice.items.forEach((item) => {
        remapInvoiceSelectedOptions(item.selectedOptions, remapCatalog, item.itemId);
      });
    });
  }

  const stillUnmapped = collectUnmappedInvoiceOptions(invoices);
  stillUnmapped.forEach((option) => {
    optionWarnings.push(
      `Không thể tạo/map option "${option.optionName}" — cần tạo thủ công trong Quản lý món/tùy chọn.`,
    );
    autoCreatedOptions.errors.push(option.optionName);
  });

  return { autoCreatedOptions, optionWarnings, createdCatalog };
};

export class CukcukImportService {
  /**
   * Mặc định ghi vào collection CUKCUK riêng (cukcuk_*), không đụng bảng iPOS.
   * Inject service khác chỉ dùng trong test.
   */
  constructor(
    private readonly dishService = createCukcukDishService(),
    private readonly customizationService = createCukcukCustomizationService(),
    private readonly invoiceNotificationService = createCukcukInvoiceNotificationService(),
    private readonly importHistoryService = new CukcukImportHistoryService(),
  ) {}

  private async logMenuImportHistory(
    result: CukcukMenuImportResult,
    context?: CukcukImportLogContext,
  ): Promise<string | undefined> {
    if (!isSuccessfulMenuImport(result)) return undefined;

    const history = await this.importHistoryService.addRecord({
      type: 'menu',
      fileName: context?.fileName || 'unknown-menu.xls',
      fileHash: context?.fileHash,
      fileSize: context?.fileSize,
      importedAt: new Date(),
      counts: buildMenuImportHistoryCounts(result),
    });

    return history.id;
  }

  private async logInvoiceImportHistory(
    result: CukcukInvoiceImportResult,
    context?: CukcukImportLogContext,
  ): Promise<string | undefined> {
    if (!isSuccessfulInvoiceImport(result)) return undefined;

    const history = await this.importHistoryService.addRecord({
      type: 'invoice',
      fileName: context?.fileName || 'unknown-invoice.xls',
      fileHash: context?.fileHash,
      fileSize: context?.fileSize,
      importedAt: new Date(),
      counts: buildInvoiceImportHistoryCounts(result),
    });

    return history.id;
  }

  /**
   * Lưu món chính, option (dạng món ảo) và quan hệ customization từ parse menu CUKCUK.
   * Upsert theo mã món, fallback theo tên đã chuẩn hóa.
   */
  async importMenuFromCukcuk(
    parsedData: CukcukParseResult,
    context?: CukcukImportLogContext,
  ): Promise<CukcukMenuImportResult> {
    const dishesResult: CukcukImportItemResult = { added: 0, updated: 0, errors: [] };
    const optionsResult: CukcukImportItemResult = { added: 0, updated: 0, errors: [] };
    const relationsResult: CukcukImportItemResult = { added: 0, updated: 0, errors: [] };

    const existingDishes = await this.dishService.getAllDishes();
    const dishIndexes = buildDishIndexes(existingDishes);
    const defaultCity = parsedData.dishes[0]?.dish.city || DEFAULT_CITY;

    for (const parsedDish of parsedData.dishes) {
      const dish = parsedDishToDish(parsedDish);
      await upsertDish(this.dishService, dishIndexes, dish, 'Món chính', dishesResult);
    }

    for (const option of parsedData.options) {
      const dish = menuOptionToDish(option, defaultCity);
      await upsertDish(this.dishService, dishIndexes, dish, 'Option', optionsResult);
    }

    const existingCustomizations = await this.customizationService.getAllCustomizations();
    const dishCodes = Array.from(new Set(parsedData.dishOptions.map((item) => item.dishCode)));

    for (const dishCode of dishCodes) {
      try {
        const groups = buildCustomizationGroups(parsedData.dishOptions, dishCode, dishIndexes);
        if (!groups.length) continue;

        const mainDish = findDishByCodeOrName(dishIndexes, dishCode);
        const customization: Customization = {
          name: customizationNameForDish(dishCode),
          city: mainDish?.city || defaultCity,
          dishCode: [dishCode],
          groups,
        };

        const existing = findCustomizationForDish(existingCustomizations, dishCode);
        if (existing?.id) {
          await this.customizationService.updateCustomization(existing.id, customization);
          relationsResult.updated += 1;
        } else {
          await this.customizationService.addCustomization(customization);
          relationsResult.added += 1;
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        relationsResult.errors.push(`Quan hệ món ${dishCode}: ${message}`);
      }
    }

    const result: CukcukMenuImportResult = {
      dishes: dishesResult,
      options: optionsResult,
      relations: relationsResult,
      errors: [...dishesResult.errors, ...optionsResult.errors, ...relationsResult.errors],
    };

    result.historyId = await this.logMenuImportHistory(result, {
      fileName: context?.fileName || parsedData.meta.fileName,
      fileHash: context?.fileHash,
      fileSize: context?.fileSize,
    });

    return result;
  }

  /**
   * Lưu hóa đơn CUKCUK: mỗi dòng món chính → notification, kèm selectedOptions đã map.
   * Upsert theo lineKey; fallback theo invoiceCode + itemId.
   */
  async importInvoicesFromCukcuk(
    parsedInvoices: CukcukParsedInvoicesInput,
    context?: CukcukImportLogContext & { allowAutoCreateOptions?: boolean },
  ): Promise<CukcukInvoiceImportResult> {
    const invoices = normalizeParsedInvoices(parsedInvoices);

    const { autoCreatedOptions, optionWarnings } = await prepareInvoicesWithAutoCreatedOptions(
      this.dishService,
      this.customizationService,
      invoices,
      DEFAULT_CITY,
      context?.allowAutoCreateOptions !== false,
    );

    const notifications = cukcukInvoicesToNotifications(invoices);

    const result: CukcukInvoiceImportResult = {
      added: 0,
      updated: 0,
      skipped: 0,
      lineItems: notifications.length,
      options: notifications.reduce((sum, row) => sum + (row.selectedOptions?.length || 0), 0),
      autoCreatedOptions,
      optionWarnings,
      errors: [],
    };

    if (!notifications.length) {
      return result;
    }

    const upsertResult = await this.invoiceNotificationService.batchUpsertCukcukNotifications(
      notifications,
    );

    result.added = upsertResult.added;
    result.updated = upsertResult.updated;
    result.skipped = upsertResult.skipped;
    result.errors = [
      ...upsertResult.errors,
      ...autoCreatedOptions.errors.map((name) => `Option chưa xử lý được: ${name}`),
    ];

    const fileName =
      context?.fileName ||
      (Array.isArray(parsedInvoices) ? undefined : parsedInvoices.meta?.fileName);

    result.historyId = await this.logInvoiceImportHistory(result, {
      fileName,
      fileHash: context?.fileHash,
      fileSize: context?.fileSize,
    });

    return result;
  }
}

export { collectUnmappedInvoiceOptions, prepareInvoicesWithAutoCreatedOptions };

const defaultCukcukImportService = new CukcukImportService();

export const importMenuFromCukcuk = (
  parsedData: CukcukParseResult,
  context?: CukcukImportLogContext,
): Promise<CukcukMenuImportResult> =>
  defaultCukcukImportService.importMenuFromCukcuk(parsedData, context);

export const importInvoicesFromCukcuk = (
  parsedInvoices: CukcukParsedInvoicesInput,
  context?: CukcukImportLogContext & { allowAutoCreateOptions?: boolean },
): Promise<CukcukInvoiceImportResult> =>
  defaultCukcukImportService.importInvoicesFromCukcuk(parsedInvoices, context);

/** Parse + lưu file DANHMUCTHUCDON CUKCUK (món, option, quan hệ). */
export const importCukcukMenuFile = async (
  file: File,
  parseOptions: ParseCukcukMenuOptions = {},
): Promise<CukcukMenuFileImportResult> => {
  const parsed = await parseCukcukMenu(file, parseOptions);
  const fileHash = await computeFileFingerprint(file);
  const importResult = await defaultCukcukImportService.importMenuFromCukcuk(parsed, {
    fileName: parsed.meta.fileName,
    fileHash,
    fileSize: file.size,
  });

  return {
    ...importResult,
    parseWarnings: parsed.meta.warnings,
    fileName: parsed.meta.fileName,
  };
};

/** Parse + map option + lưu file chi tiết doanh thu CUKCUK. */
export const importCukcukInvoiceFile = async (
  file: File,
  sources: Customization[] | OptionCatalogSources,
  context?: CukcukImportLogContext & { allowAutoCreateOptions?: boolean },
): Promise<CukcukInvoiceFileImportResult> => {
  const catalog = Array.isArray(sources)
    ? buildMenuOptionCatalogFromCustomizations(sources)
    : resolveOptionCatalog(sources);

  const parsed = await parseCukcukInvoicesWithOptionMapping(file, catalog);
  const importResult = await defaultCukcukImportService.importInvoicesFromCukcuk(parsed, {
    fileName: parsed.meta.fileName,
    fileHash: context?.fileHash,
    fileSize: context?.fileSize ?? file.size,
    allowAutoCreateOptions: context?.allowAutoCreateOptions,
  });

  const mappingWarnings = [...parsed.meta.warnings, ...importResult.optionWarnings];
  if (!catalog.length) {
    mappingWarnings.unshift(
      'Catalog option trống — các option thiếu sẽ được tự tạo (giá 0) khi lưu hóa đơn.',
    );
  }

  return {
    ...importResult,
    mappingWarnings,
    fileName: parsed.meta.fileName,
  };
};